import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useThemeStore } from '../stores/themeStore'
import { useAuth } from '../core/AuthContext'
import Icon from './Icon'
import Logo from './Logo'

const AUTHENTICATED_NAV = [
  { to: '/home', icon: 'home', label: 'Home', end: true },
  { to: '/reports', icon: 'format_list_bulleted', label: 'My Reports' },
  { to: '/settings', icon: 'settings', label: 'Settings' },
]

const GUEST_NAV = [
  { to: '/report/new', icon: 'add_circle', label: 'New Report' },
]

export default function AppShell() {
  const { theme, toggleTheme } = useThemeStore()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const NAV_ITEMS = isAuthenticated
    ? [{ to: '/report/new', icon: 'add_circle', label: 'New Report' }, ...AUTHENTICATED_NAV]
    : GUEST_NAV

  return (
    <div className="min-h-screen bg-surface-dim text-text-primary pb-24 md:pb-0">
      {/* Top App Bar */}
      <header className="fixed top-0 inset-x-0 z-40 bg-surface-dark/95 backdrop-blur-md border-b border-border-subtle">
        <div className="max-w-5xl mx-auto h-16 px-4 md:px-10 flex items-center justify-between">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center min-h-0"
            aria-label="Snagmatic home"
          >
            <Logo height={30} />
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-label-md transition-colors ${
                    isActive
                      ? 'bg-accent/15 text-accent'
                      : 'text-text-secondary hover:text-accent hover:bg-surface-high'
                  }`
                }
              >
                <Icon name={item.icon} className="text-xl" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            onClick={toggleTheme}
            className="p-2 min-h-0 rounded-full text-text-secondary hover:bg-surface-high active:scale-95 transition"
            aria-label="Toggle theme"
          >
            <Icon name={theme === 'dark' ? 'dark_mode' : 'light_mode'} className="text-xl" />
          </button>
        </div>
      </header>

      {/* Page content */}
      <main className="pt-16 max-w-5xl mx-auto">
        <Outlet />
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 flex justify-around items-center px-2 py-2 bg-surface-low border-t border-border">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 rounded-full min-w-0 transition ${
                isActive ? 'text-accent' : 'text-text-muted hover:text-accent'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon name={item.icon} filled={isActive} className="text-2xl" />
                <span className="text-label-sm">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
